/** Typed internal-cost reporting and chargeback approval data access. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { compactParams, usePagedQuery, type Paged } from "@/shared/lib/paging";

export type CostRow = Schemas["InternalCostRow"];
export type Chargeback = Schemas["ChargebackOut"];
export type InternalCostReport = Schemas["InternalCostReport"];

export interface CostFilters {
  site_id?: string;
  vessel_id?: string;
  asset_id?: string;
  team_id?: string;
  operating_unit_id?: string;
  cost_center_id?: string;
  fault_id?: string;
  since?: string;
  until?: string;
  scope?: "owned" | "external" | "all";
}

/** Booked internal costs, restricted by the server to authorized tenant/sites. */
export function useInternalCosts(
  filters: CostFilters,
  enabled = true,
): UseQueryResult<InternalCostReport> {
  return useQuery({
    queryKey: ["billing", "internal-costs", filters],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reporting/cost/internal", {
          params: { query: compactParams({ ...filters }) },
        }),
      ),
    enabled,
  });
}

/** Download a permission-checked CSV or Excel file without bypassing API tenant/CSRF middleware. */
export async function exportInternalCosts(
  filters: CostFilters,
  format: "csv" | "xlsx" = "csv",
): Promise<void> {
  const blob = unwrap(
    await api.GET("/api/v1/reporting/cost/internal", {
      params: { query: { ...compactParams({ ...filters }), format } },
      parseAs: "blob",
    }),
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `internal-maintenance-costs.${format}`;
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(url);
  }
}

/** Internal chargeback register. */
export function useChargebacks(enabled = true, status?: string): Paged<Chargeback> {
  return usePagedQuery(
    ["billing", "chargebacks", status],
    async (cursor) =>
      unwrap(
        await api.GET("/api/v1/billing/chargebacks", {
          params: { query: compactParams({ cursor, limit: 50, status }) },
        }),
      ),
    { enabled },
  );
}

/** Load the immutable source snapshot and approval details. */
export function useChargeback(id: string | undefined): UseQueryResult<Chargeback> {
  return useQuery({
    queryKey: ["billing", "chargeback", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/billing/chargebacks/{chargeback_id}", {
          params: { path: { chargeback_id: id ?? "" } },
        }),
      ),
    enabled: !!id,
  });
}

/** Snapshot all accepted inspection/repair costs for one linked fault chain. */
export function useCreateChargeback(): UseMutationResult<
  Chargeback,
  Error,
  Schemas["ChargebackIn"]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => unwrap(await api.POST("/api/v1/billing/chargebacks", { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}

/** Independently approve the immutable snapshot; server rejects stale/reopened sources. */
export function useApproveChargeback(): UseMutationResult<
  Chargeback,
  Error,
  { id: string; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) =>
      unwrap(
        await api.POST("/api/v1/billing/chargebacks/{chargeback_id}/approve", {
          params: { path: { chargeback_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}

/** Void a stale draft and permit a replacement, preserving all original snapshot evidence. */
export function useVoidChargeback(): UseMutationResult<
  Chargeback,
  Error,
  { id: string; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) =>
      unwrap(
        await api.POST("/api/v1/billing/chargebacks/{chargeback_id}/void", {
          params: { path: { chargeback_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}
